import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SortOrder, listFiltersService, ListTableSort, NamedProperty, depascalize } from "sprint-common-web";

import { TrailerFilter } from "../../../../common/components/trailer-info/TrailerFilter";
import { CloudAssetOrigin } from "../../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../../common/types/AssetStatus";
import { AssetType } from "../../../../common/types/AssetType";
import { RecordType } from "../../../../common/types/RecordType";
import { SingleTvSeriesContext } from "../context/SingleTvSeriesContext";
import { useSingleTvSeriesDetails } from "./useSingleTvSeriesDetails";

export interface TvSeriesAssetsListUrlFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    season?: string;
    episode?: string;
    versionTypes?: string[];
    distributors?: string[];
    brandings?: string[];
    statuses?: number[];
    types?: number[];
    recordTypes?: number[];
    origins?: number[];
    createdDateFrom?: Date;
    createdDateTo?: Date;
    updatedDateFrom?: Date;
    updatedDateTo?: Date;
    trailers?: number[];
}

const selectedTrailers = [TrailerFilter.Trailer, TrailerFilter.NonTrailer];

const defaultFilters: TvSeriesAssetsListUrlFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "updatedAudit",
    sortOrder: "desc",
    season: undefined,
    episode: undefined,
    versionTypes: undefined,
    distributors: undefined,
    brandings: undefined,
    statuses: [AssetStatus.Active],
    types: [AssetType.Cloud, AssetType.OnPrem],
    recordTypes: [RecordType.Video, RecordType.Audio, RecordType.Subtitle],
    origins: [CloudAssetOrigin.User, CloudAssetOrigin.System],
    trailers: selectedTrailers
};

export const useTvSeriesAssetsListUrlFilters = () => {
    const filtersService = useMemo(() => listFiltersService<TvSeriesAssetsListUrlFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const { getVersionDetails } = useSingleTvSeriesDetails();

    const [filters, setFilters] = useState<TvSeriesAssetsListUrlFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    const { tvSeries } = useContext(SingleTvSeriesContext);

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [filters]);

    const setTrailers = (trailers?: number[]): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({
            ...prevFilters,
            trailers,
            page: 1
        }));
    };

    const setSort = ({ by: sortBy, order: sortOrder }: ListTableSort) => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, sortBy, sortOrder, page: 1 }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setSeasonId = (seasonId: string | undefined) => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => {
            if (prevFilters.season === seasonId) {
                return prevFilters;
            }

            const episodeId = undefined;

            const { versionTypes, distributors, brandings } = getVersionDetails(tvSeries, seasonId, episodeId);

            const correctVersionTypes = prevFilters.versionTypes?.filter((versionTypeId: string) =>
                isCorrectVersionType(versionTypeId, versionTypes)
            );

            const correctDistributors = prevFilters.distributors?.filter((distributorId: string) =>
                isCorrectDistributor(distributorId, distributors)
            );

            const correctBrandings = prevFilters.brandings?.filter((brandingId: string) =>
                isCorrectBranding(brandingId, brandings)
            );

            return {
                ...prevFilters,
                season: seasonId,
                episode: episodeId,
                versionTypes: correctVersionTypes,
                distributors: correctDistributors,
                brandings: correctBrandings
            };
        });
    };

    const setEpisodeId = (episodeId: string | undefined) => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => {
            if (prevFilters.episode === episodeId) {
                return prevFilters;
            }

            const seasonId = prevFilters.season;

            const { versionTypes, distributors, brandings } = getVersionDetails(tvSeries, seasonId, episodeId);

            const correctVersionTypes = prevFilters.versionTypes?.filter((versionTypeId: string) =>
                isCorrectVersionType(versionTypeId, versionTypes)
            );

            const correctDistributors = prevFilters.distributors?.filter((distributorId: string) =>
                isCorrectDistributor(distributorId, distributors)
            );

            const correctBrandings = prevFilters.brandings?.filter((brandingId: string) =>
                isCorrectBranding(brandingId, brandings)
            );

            return {
                ...prevFilters,
                episode: episodeId,
                versionTypes: correctVersionTypes,
                distributors: correctDistributors,
                brandings: correctBrandings
            };
        });
    };

    const setVersionsTypeIds = (versionTypeIds: string[] | undefined): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, versionTypes: versionTypeIds }));
    };

    const setAssetTypes = (types?: number[]): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, types, page: 1 }));
    };

    const setAssetRecordTypes = (recordTypes?: number[]): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, recordTypes, page: 1 }));
    };

    const setAssetOrigins = (origins?: number[]): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, origins, page: 1 }));
    };

    const setDistributorIds = (distributorIds: string[] | undefined): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, distributors: distributorIds }));
    };

    const setBrandingIds = (brandingIds: string[] | undefined): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, brandings: brandingIds }));
    };

    const setStatusIds = (statusIds: number[]): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, statuses: statusIds, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const setCreatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, createdDateFrom: date, page: 1 }));
    };

    const setCreatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, createdDateTo: date, page: 1 }));
    };

    const setUpdatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, updatedDateFrom: date, page: 1 }));
    };

    const setUpdatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: TvSeriesAssetsListUrlFilters) => ({ ...prevFilters, updatedDateTo: date, page: 1 }));
    };

    const trailersOptions = selectedTrailers.map((type) => ({
        key: type,
        text: depascalize(TrailerFilter[type])
    }));

    return {
        filters,
        trailersOptions,
        setTrailers,
        setSeasonId,
        setEpisodeId,
        setVersionsTypeIds,
        setDistributorIds,
        setBrandingIds,
        setStatusIds,
        setSort,
        setPage,
        setPageSize,
        clearFilters,
        setAssetTypes,
        setAssetRecordTypes,
        setAssetOrigins,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo
    };
};

const isCorrectVersionType = (versionTypeId: string, versionTypes: NamedProperty[]): boolean => {
    return versionTypes.some((versionType: NamedProperty) => versionType.id === versionTypeId);
};

const isCorrectDistributor = (distributorId: string, distributors: NamedProperty[]): boolean => {
    return distributors.some((distributor: NamedProperty) => distributor.id === distributorId);
};

const isCorrectBranding = (brandingId: string, brandings: NamedProperty[]): boolean => {
    return brandings.some((branding: NamedProperty) => branding.id === brandingId);
};
